// What a map scene needs that can be computed without Three.js (in the map
// loading worker): the lightmaps packed into an atlas and the batched geometry.

import type { Bsp, BspSurface } from '../shared/bsp';
import type { Entity } from '../shared/entities';
import { hiddenEntities } from '../shared/game/mapScript';
import { loadFogs, type MapFogs } from './fogVolumes';
import { loadLightmapImages, packLightmaps, type LightmapImage, type LightmapLayout } from './lightmapAtlas';
import { GeometryBuilder, type MapGeometry } from './mapGeometry';
import type { ShaderDef } from './shaderScripts';

/** What the map loading worker prepares off the main thread. */
export interface PreparedScene {
  geometry: MapGeometry;
  /** The lightmap atlas, or the single lightmaps when they did not pack. */
  atlas: LightmapLayout | null;
  lightmaps: (LightmapImage | null)[] | null;
}

/** The fog of a surface: its fog volume, else the global fog (-2 = global, -1 = none). */
export function surfaceFog(fogs: MapFogs, surf: BspSurface): number {
  if (surf.fogNum >= 0 && fogs.volumes[surf.fogNum]) return surf.fogNum;
  return fogs.global ? -2 : -1;
}

/** Lightmaps, atlas and geometry of a map (the same in the worker and on the main thread). */
export async function prepareScene(bsp: Bsp, mapName: string, entities: Entity[], shaders: Map<string, ShaderDef>, read: (path: string) => Promise<Uint8Array | null>, maxTextureSize = 4096): Promise<PreparedScene> {
  const images = await loadLightmapImages(bsp, mapName, read);
  const atlas = packLightmaps(images, Math.min(4096, maxTextureSize));
  const fogs = loadFogs(bsp, shaders, entities.find((e) => e.classname === 'worldspawn'));
  const script = await read(`maps/${mapName}.script`);
  const hidden = hiddenEntities(entities, script ? new TextDecoder('latin1').decode(script) : null);
  const geometry = new GeometryBuilder({ bsp, shaders, entities, fogOf: (s) => surfaceFog(fogs, s), atlas, hidden }).build();
  return { geometry, atlas: atlas ? { ...atlas, tiles: atlas.tiles } : null, lightmaps: atlas ? null : images };
}

